import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../shared/Layout';
import PageHeader from '../shared/PageHeader';
import IconGoBack from '../../components/icons/IconGoBack';
import IconAdd from '../../components/icons/IconAdd';
import CommunicationTypePicker from './CommunicationTypePicker';
import NotificationBlockCard from './NotificationBlockCard';
import { parseNotificationContent, serializeNotificationBlocks, renderNotificationBlocks } from './renderNotificationBlocks';
import { NOTIFICATION_BLOCK_TYPE_LIST } from '../../variables/notificationBlockTypes';
import * as api from './api';
import '../../css/exercise-templates.css';
import '../../css/communications.css';

let blockKeySeq = 1;
function newBlockId() { return 'b' + (blockKeySeq++) + '-' + Date.now(); }

// Two save paths, on purpose:
//  - Title and type are saved only by the header "Зберегти" button.
//  - Blocks are saved by themselves: a block's own save button, and adding,
//    moving or deleting a block, write the whole announcement right away.
// The backend replaces the whole row on PUT, so every write sends title, type
// and content together - title and type are the last values the server has,
// so an unsaved title edit is never written by a block action.
// Blocks are one JSON field (Notification.content), so each write serializes
// the full block list. Writes go through one queue, so an earlier request can
// never land after a later one and overwrite it.
export default function CommunicationBuilder() {
   const { id } = useParams();
   const navigate = useNavigate();
   const [title, setTitle] = useState('');
   const [type, setType] = useState('INFO');
   const [blocks, setBlocks] = useState([]);
   const [savedTitle, setSavedTitle] = useState('');
   const [savedType, setSavedType] = useState('INFO');
   const [status, setStatus] = useState('loading');
   const [saving, setSaving] = useState(false);
   const [saveError, setSaveError] = useState('');
   const [addPickerOpen, setAddPickerOpen] = useState(false);
   // Students this announcement is assigned to - read-only list under the
   // builder, each row opens that student's card.
   const [recipients, setRecipients] = useState([]);
   const [recipientsStatus, setRecipientsStatus] = useState('loading');

   // Block handlers read the latest list through this ref, so two quick
   // actions in a row can't compute their change from a stale list.
   const blocksRef = useRef([]);
   const writeQueueRef = useRef(Promise.resolve());

   const titleDirty = title !== savedTitle || type !== savedType;

   const commitBlocks = (next) => {
      blocksRef.current = next;
      setBlocks(next);
   };

   const loadRecipients = () => {
      api.getNotificationStudents(id)
         .then((res) => {
            setRecipients(Array.isArray(res.data) ? res.data : []);
            setRecipientsStatus('ready');
         })
         .catch(() => setRecipientsStatus('error'));
   };

   useEffect(() => {
      loadRecipients();
      api.getNotification(id)
         .then((res) => {
            const loadedTitle = res.data.title || '';
            const loadedType = res.data.type || 'INFO';
            const loadedBlocks = parseNotificationContent(res.data.content);
            setTitle(loadedTitle);
            setType(loadedType);
            setSavedTitle(loadedTitle);
            setSavedType(loadedType);
            commitBlocks(loadedBlocks);
            setStatus('ready');
         })
         .catch((err) => {
            if (err.response && err.response.status === 401) navigate('/login');
            setStatus('error');
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id]);

   // Sends one full write behind every write already queued. Resolves true
   // when the server accepted it; a failure leaves the on-screen state as it
   // is, so nothing the user typed disappears, and shows a message instead.
   const persist = (payload) => {
      const job = writeQueueRef.current.then(() => api.updateNotification(id, payload).then(() => true));
      writeQueueRef.current = job.catch(() => false);
      return job
         .then((ok) => { setSaveError(''); return ok; })
         .catch((err) => {
            if (err && err.response && err.response.status === 401) navigate('/login');
            setSaveError('Не вдалося зберегти. Перевірте з\'єднання і спробуйте ще раз.');
            return false;
         });
   };

   const persistBlocks = (nextBlocks) => persist({
      title: savedTitle,
      type: savedType,
      content: serializeNotificationBlocks(nextBlocks),
   });

   const addBlock = (blockType) => {
      const def = NOTIFICATION_BLOCK_TYPE_LIST.find((t) => t.type === blockType);
      const next = [...blocksRef.current, { id: newBlockId(), type: blockType, ...(def ? def.defaultConfig : {}) }];
      commitBlocks(next);
      setAddPickerOpen(false);
      persistBlocks(next);
   };
   const saveBlock = (index, updated) => {
      const next = blocksRef.current.map((b, i) => (i === index ? updated : b));
      commitBlocks(next);
      persistBlocks(next);
   };
   const moveBlock = (index, dir) => {
      const current = blocksRef.current;
      const target = index + dir;
      if (target < 0 || target >= current.length) return;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      commitBlocks(next);
      persistBlocks(next);
   };
   const deleteBlock = (index) => {
      const next = blocksRef.current.filter((_, i) => i !== index);
      commitBlocks(next);
      persistBlocks(next);
   };

   const handleSave = () => {
      if (saving || !titleDirty) return;
      setSaving(true);
      const nextTitle = title;
      const nextType = type;
      persist({ title: nextTitle, type: nextType, content: serializeNotificationBlocks(blocksRef.current) })
         .then((ok) => {
            if (ok) {
               setSavedTitle(nextTitle);
               setSavedType(nextType);
            }
         })
         .finally(() => setSaving(false));
   };

   // Leaving with an unsaved title or type would drop them silently, so ask first.
   const openStudent = (studentId) => {
      if (titleDirty && !window.confirm('Є незбережені зміни назви або типу. Перейти без збереження?')) return;
      navigate('/students/' + studentId);
   };

   const renderRecipients = () => {
      const sorted = [...recipients].sort((a, b) => {
         const sa = (a.student && a.student.surname) || '';
         const sb = (b.student && b.student.surname) || '';
         return sa.localeCompare(sb, 'uk');
      });
      return (
         <section className='communication-recipients'>
            <div className='communication-recipients-head'>
               <span className='exercise-block-field-label'>Студенти, яким додано цю інформацію</span>
               <span className='communication-recipients-count'>{recipients.length}</span>
            </div>
            {recipientsStatus === 'loading' ? (
               <p className='communication-recipients-empty'>Завантаження...</p>
            ) : recipientsStatus === 'error' ? (
               <p className='communication-recipients-empty'>Не вдалося завантажити список студентів</p>
            ) : sorted.length === 0 ? (
               <p className='communication-recipients-empty'>Цю інформацію ще нікому не додано</p>
            ) : (
               <div className='communication-recipients-grid'>
                  {sorted.map((sn) => {
                     const s = sn.student || {};
                     const name = [s.name, s.surname].filter(Boolean).join(' ') || 'Без імені';
                     const notifType = (sn.notification && sn.notification.type) || type;
                     const needsAcceptance = notifType === 'ACK_REQUIRED' || notifType === 'BLOCKING';
                     return (
                        <div key={sn.id} className='communication-recipient' onClick={() => openStudent(s.id)} title='Відкрити картку студента'>
                           <div className='communication-recipient-main'>
                              <span className='communication-recipient-name'>{name}</span>
                              {s.groupe && s.groupe.name ? <span className='communication-recipient-group'>{s.groupe.name}</span> : ''}
                           </div>
                           <div className='communication-recipient-status'>
                              <span className={'communication-recipient-chip' + (sn.viewed ? ' is-seen' : '')}>
                                 {sn.viewed ? 'Переглянуто' : 'Не переглянуто'}
                              </span>
                              {needsAcceptance ? (
                                 <span className={'communication-recipient-chip ' + (sn.accepted ? 'is-ok' : 'is-wait')}>
                                    {sn.accepted ? 'Підтверджено' : 'Не підтверджено'}
                                 </span>
                              ) : ''}
                           </div>
                           <span className='communication-recipient-arrow'>›</span>
                        </div>
                     );
                  })}
               </div>
            )}
         </section>
      );
   };

   if (status === 'loading') return <Layout><main className='main-block'><div className='main-content txt-c'>Завантаження...</div></main></Layout>;
   if (status === 'error') return <Layout><main className='main-block'><div className='main-content txt-c'>Інформацію не знайдено</div></main></Layout>;

   return (
      <Layout>
         <PageHeader name={title || 'Інформація'}>
            <div className='exercise-templates-header-actions'>
               <div className='exercise-templates-add-btn' onClick={() => navigate('/communications')} title='До списку'>
                  <IconGoBack />
               </div>
               <button type='button' className='exercise-block-save-btn communication-header-save-btn' disabled={saving || !titleDirty} onClick={handleSave}>
                  {saving ? 'Збереження...' : 'Зберегти'}
               </button>
            </div>
         </PageHeader>
         <main className='main-block'>
            <div className='main-content txt-c communication-builder-page'>
               {saveError ? <p className='communication-save-error'>{saveError}</p> : ''}
               <div className='communication-builder-grid'>
                  <div className='communication-builder-form'>
                     <div className='exercise-block-field'>
                        <span className='exercise-block-field-label'>Назва</span>
                        <input className='exercise-block-generic-text' type='text'
                           value={title} onChange={(e) => setTitle(e.target.value)} />
                     </div>
                     <CommunicationTypePicker value={type} onChange={setType} showHint />

                     <p className='exercise-block-field-label' style={{ marginTop: 14 }}>Блоки інформації</p>
                     <p className='communication-blocks-autosave-hint'>Блоки зберігаються самі, кнопкою «Зберегти» у їхній картці.</p>
                     {blocks.map((block, i) => (
                        <NotificationBlockCard key={block.id} block={block}
                           canMoveUp={i > 0} canMoveDown={i < blocks.length - 1}
                           onMove={(dir) => moveBlock(i, dir)}
                           onSave={(updated) => saveBlock(i, updated)}
                           onDelete={() => deleteBlock(i)} />
                     ))}

                     <div className='exercise-block-add-trigger' onClick={() => setAddPickerOpen((prev) => !prev)}>
                        <IconAdd />
                        <span>Додати блок</span>
                     </div>
                     {addPickerOpen ? (
                        <div className='exercise-block-type-picker'>
                           <div className='exercise-block-type-row'>
                              {NOTIFICATION_BLOCK_TYPE_LIST.map((t) => (
                                 <div key={t.type} className='exercise-block-type-chip' style={{ '--accent': t.accent }} onClick={() => addBlock(t.type)}>
                                    {t.label}
                                 </div>
                              ))}
                           </div>
                        </div>
                     ) : ''}
                  </div>
                  <div className='communication-builder-preview'>
                     <p className='exercise-block-field-label'>Попередній перегляд</p>
                     <div className='communication-preview-card'>
                        <p className='communication-preview-title'>{title || 'Без назви'}</p>
                        {renderNotificationBlocks(blocks)}
                     </div>
                  </div>
               </div>
               {renderRecipients()}
            </div>
         </main>
      </Layout>
   );
}
